/**
 * Lightweight boundary profiling for the historical FIT import.
 *
 * All cross-context (main thread <-> worker) durations are derived from high-resolution
 * *epoch* timestamps, because `performance.now()` uses a different time origin inside a
 * Worker on Safari and therefore cannot be compared across contexts.
 */

export type WorkerHandoffBottleneck =
  | "worker creation"
  | "worker message delivery"
  | "application wait/race"
  | "FIT processing"
  | "inconclusive";

export type WorkerHandoffMarks = {
  selectionReceivedAtEpochMs: number | null;
  fileReadStartedAtEpochMs: number | null;
  fileReadCompletedAtEpochMs: number | null;
  workerConstructionRequestedAtEpochMs: number | null;
  workerConstructorReturnedAtEpochMs: number | null;
  workerReadyRequestedAtEpochMs: number | null;
  workerReadyReceivedAtEpochMs: number | null;
  payloadPostMessageStartedAtEpochMs: number | null;
  payloadPostMessageReturnedAtEpochMs: number | null;
  workerMessageReceivedAtEpochMs: number | null;
  workerProcessingStartedAtEpochMs: number | null;
  workerProcessingCompletedAtEpochMs: number | null;
  workerResultPostedAtEpochMs: number | null;
  mainThreadResultReceivedAtEpochMs: number | null;
  persistenceStartedAtEpochMs: number | null;
  persistenceCompletedAtEpochMs: number | null;
};

export type WorkerHandoffDurations = {
  selectionToFileReadStartMs: number | null;
  fileReadMs: number | null;
  fileReadToWorkerConstructionMs: number | null;
  workerConstructionMs: number | null;
  workerConstructionToReadyRequestMs: number | null;
  workerReadyLatencyMs: number | null;
  readyToPayloadPostMessageMs: number | null;
  payloadPostMessageSyncMs: number | null;
  payloadDeliveryLatencyMs: number | null;
  workerReceiptToProcessingStartMs: number | null;
  workerProcessingMs: number | null;
  workerProcessingCompleteToResultPostMs: number | null;
  workerResultDeliveryLatencyMs: number | null;
  resultToPersistenceStartMs: number | null;
  persistenceMs: number | null;
  totalMs: number | null;
};

export type WorkerHandoffProfile = {
  correlationId: string;
  workerInstanceId: string | null;
  fileName: string | null;
  fileBytes: number | null;
  marks: WorkerHandoffMarks;
  durations: WorkerHandoffDurations;
  bottleneck: WorkerHandoffBottleneck;
  bottleneckDetail: string | null;
  clockSkewSuspected: boolean;
};

export function epochNowMs(): number {
  if (typeof performance !== "undefined" && typeof performance.now === "function" && typeof performance.timeOrigin === "number" && Number.isFinite(performance.timeOrigin)) {
    return performance.timeOrigin + performance.now();
  }
  return Date.now();
}

let correlationSequence = 0;

export function createImportCorrelationId(prefix = "imp"): string {
  correlationSequence += 1;
  const random = Math.random().toString(36).slice(2, 8);
  return `${prefix}-${Date.now().toString(36)}-${correlationSequence.toString(36)}-${random}`;
}

export function createEmptyWorkerHandoffMarks(): WorkerHandoffMarks {
  return {
    selectionReceivedAtEpochMs: null,
    fileReadStartedAtEpochMs: null,
    fileReadCompletedAtEpochMs: null,
    workerConstructionRequestedAtEpochMs: null,
    workerConstructorReturnedAtEpochMs: null,
    workerReadyRequestedAtEpochMs: null,
    workerReadyReceivedAtEpochMs: null,
    payloadPostMessageStartedAtEpochMs: null,
    payloadPostMessageReturnedAtEpochMs: null,
    workerMessageReceivedAtEpochMs: null,
    workerProcessingStartedAtEpochMs: null,
    workerProcessingCompletedAtEpochMs: null,
    workerResultPostedAtEpochMs: null,
    mainThreadResultReceivedAtEpochMs: null,
    persistenceStartedAtEpochMs: null,
    persistenceCompletedAtEpochMs: null
  };
}

export function createWorkerHandoffProfile(input: {
  correlationId: string;
  fileName?: string | null;
  fileBytes?: number | null;
}): WorkerHandoffProfile {
  return {
    correlationId: input.correlationId,
    workerInstanceId: null,
    fileName: input.fileName ?? null,
    fileBytes: input.fileBytes ?? null,
    marks: createEmptyWorkerHandoffMarks(),
    durations: computeWorkerHandoffDurations(createEmptyWorkerHandoffMarks()),
    bottleneck: "inconclusive",
    bottleneckDetail: null,
    clockSkewSuspected: false
  };
}

function delta(from: number | null, to: number | null): number | null {
  if (from === null || to === null || !Number.isFinite(from) || !Number.isFinite(to)) {
    return null;
  }
  return to - from;
}

function clampNonNegative(value: number | null): number | null {
  if (value === null) return null;
  return value < 0 ? 0 : value;
}

export function computeWorkerHandoffDurations(marks: WorkerHandoffMarks): WorkerHandoffDurations {
  const totalStart = marks.selectionReceivedAtEpochMs ?? marks.fileReadStartedAtEpochMs;
  const totalEnd = marks.persistenceCompletedAtEpochMs ?? marks.mainThreadResultReceivedAtEpochMs;

  return {
    selectionToFileReadStartMs: clampNonNegative(delta(marks.selectionReceivedAtEpochMs, marks.fileReadStartedAtEpochMs)),
    fileReadMs: clampNonNegative(delta(marks.fileReadStartedAtEpochMs, marks.fileReadCompletedAtEpochMs)),
    fileReadToWorkerConstructionMs: clampNonNegative(delta(marks.fileReadCompletedAtEpochMs, marks.workerConstructionRequestedAtEpochMs)),
    workerConstructionMs: clampNonNegative(delta(marks.workerConstructionRequestedAtEpochMs, marks.workerConstructorReturnedAtEpochMs)),
    workerConstructionToReadyRequestMs: clampNonNegative(delta(marks.workerConstructorReturnedAtEpochMs, marks.workerReadyRequestedAtEpochMs)),
    workerReadyLatencyMs: clampNonNegative(delta(marks.workerReadyRequestedAtEpochMs, marks.workerReadyReceivedAtEpochMs)),
    readyToPayloadPostMessageMs: clampNonNegative(delta(marks.workerReadyReceivedAtEpochMs, marks.payloadPostMessageStartedAtEpochMs)),
    payloadPostMessageSyncMs: clampNonNegative(delta(marks.payloadPostMessageStartedAtEpochMs, marks.payloadPostMessageReturnedAtEpochMs)),
    payloadDeliveryLatencyMs: delta(marks.payloadPostMessageStartedAtEpochMs, marks.workerMessageReceivedAtEpochMs),
    workerReceiptToProcessingStartMs: delta(marks.workerMessageReceivedAtEpochMs, marks.workerProcessingStartedAtEpochMs),
    workerProcessingMs: clampNonNegative(delta(marks.workerProcessingStartedAtEpochMs, marks.workerProcessingCompletedAtEpochMs)),
    workerProcessingCompleteToResultPostMs: clampNonNegative(delta(marks.workerProcessingCompletedAtEpochMs, marks.workerResultPostedAtEpochMs)),
    workerResultDeliveryLatencyMs: delta(marks.workerResultPostedAtEpochMs, marks.mainThreadResultReceivedAtEpochMs),
    resultToPersistenceStartMs: clampNonNegative(delta(marks.mainThreadResultReceivedAtEpochMs, marks.persistenceStartedAtEpochMs)),
    persistenceMs: clampNonNegative(delta(marks.persistenceStartedAtEpochMs, marks.persistenceCompletedAtEpochMs)),
    totalMs: clampNonNegative(delta(totalStart, totalEnd))
  };
}

function maxDefined(...values: Array<number | null | undefined>): number | null {
  const defined = values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  if (defined.length === 0) return null;
  return Math.max(...defined);
}

function sumDefined(...values: Array<number | null | undefined>): number | null {
  const defined = values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  if (defined.length === 0) return null;
  return defined.reduce((total, value) => total + value, 0);
}

const DOMINANCE_RATIO = 0.4;

export function classifyWorkerHandoffBottleneck(profile: Pick<WorkerHandoffProfile, "durations">): {
  bottleneck: WorkerHandoffBottleneck;
  bottleneckDetail: string | null;
} {
  const { durations } = profile;
  const total = durations.totalMs;

  const buckets: Array<{ bottleneck: WorkerHandoffBottleneck; ms: number | null }> = [
    {
      bottleneck: "worker creation",
      ms: sumDefined(durations.workerConstructionMs, durations.workerConstructionToReadyRequestMs, durations.workerReadyLatencyMs)
    },
    {
      bottleneck: "worker message delivery",
      ms: maxDefined(durations.payloadDeliveryLatencyMs, durations.workerResultDeliveryLatencyMs)
    },
    {
      bottleneck: "application wait/race",
      ms: sumDefined(durations.selectionToFileReadStartMs, durations.readyToPayloadPostMessageMs, durations.resultToPersistenceStartMs)
    },
    {
      bottleneck: "FIT processing",
      ms: sumDefined(durations.workerReceiptToProcessingStartMs, durations.workerProcessingMs)
    }
  ];

  const measured = buckets.filter((bucket): bucket is { bottleneck: WorkerHandoffBottleneck; ms: number } => typeof bucket.ms === "number" && Number.isFinite(bucket.ms));
  if (measured.length === 0 || total === null || total <= 0) {
    return { bottleneck: "inconclusive", bottleneckDetail: "Not enough measured boundaries to classify." };
  }

  const largest = measured.reduce((current, candidate) => (candidate.ms > current.ms ? candidate : current));
  const share = largest.ms / total;
  if (share < DOMINANCE_RATIO) {
    return {
      bottleneck: "inconclusive",
      bottleneckDetail: `No single stage exceeded ${Math.round(DOMINANCE_RATIO * 100)}% of ${Math.round(total)}ms total (largest: ${largest.bottleneck} at ${Math.round(largest.ms)}ms).`
    };
  }

  return {
    bottleneck: largest.bottleneck,
    bottleneckDetail: `${Math.round(largest.ms)}ms of ${Math.round(total)}ms total (${Math.round(share * 100)}%).`
  };
}

/** Cross-context deltas below this threshold indicate misaligned epoch clocks rather than real negative time. */
const CLOCK_SKEW_TOLERANCE_MS = -25;

export function finalizeWorkerHandoffProfile(profile: WorkerHandoffProfile): WorkerHandoffProfile {
  const durations = computeWorkerHandoffDurations(profile.marks);
  const crossContextValues = [
    durations.payloadDeliveryLatencyMs,
    durations.workerReceiptToProcessingStartMs,
    durations.workerResultDeliveryLatencyMs
  ];
  const clockSkewSuspected = crossContextValues.some(value => typeof value === "number" && value < CLOCK_SKEW_TOLERANCE_MS);
  const next: WorkerHandoffProfile = { ...profile, durations, clockSkewSuspected };
  const { bottleneck, bottleneckDetail } = classifyWorkerHandoffBottleneck(next);
  return { ...next, bottleneck, bottleneckDetail };
}

export function withWorkerHandoffMarks(profile: WorkerHandoffProfile, patch: Partial<WorkerHandoffMarks>): WorkerHandoffProfile {
  return finalizeWorkerHandoffProfile({
    ...profile,
    marks: { ...profile.marks, ...patch }
  });
}

export type ImportTimeAttribution = {
  transportMs: number;
  processingMs: number;
  persistenceMs: number;
  otherMs: number;
  totalMs: number;
  dominant: "transport" | "processing" | "persistence" | "other" | "unknown";
};

/**
 * Splits measured import time into transport vs processing so the general performance panel
 * cannot report "worker transfer" for time that was actually spent decoding FIT data.
 */
export function classifyImportTimeAttribution(input: {
  totalElapsedMs: number | null;
  mainThreadToWorkerPostMs: number | null;
  workerReceiveLatencyMs: number | null;
  workerResultTransferMs: number | null;
  fitDecodeMs: number | null;
  recordExtractionMs: number | null;
  normalizationMs: number | null;
  analyticsMs: number | null;
  persistenceMs: number | null;
}): ImportTimeAttribution {
  const add = (...values: Array<number | null | undefined>): number =>
    values.reduce<number>((total, value) => (typeof value === "number" && Number.isFinite(value) ? total + value : total), 0);

  const transportMs = add(input.mainThreadToWorkerPostMs, input.workerReceiveLatencyMs, input.workerResultTransferMs);
  const processingMs = add(input.fitDecodeMs, input.recordExtractionMs, input.normalizationMs, input.analyticsMs);
  const persistenceMs = add(input.persistenceMs);
  const totalMs = typeof input.totalElapsedMs === "number" && Number.isFinite(input.totalElapsedMs) ? input.totalElapsedMs : 0;
  const otherMs = Math.max(0, totalMs - transportMs - processingMs - persistenceMs);

  const buckets: Array<{ key: ImportTimeAttribution["dominant"]; ms: number }> = [
    { key: "transport", ms: transportMs },
    { key: "processing", ms: processingMs },
    { key: "persistence", ms: persistenceMs },
    { key: "other", ms: otherMs }
  ];
  const largest = buckets.reduce((current, candidate) => (candidate.ms > current.ms ? candidate : current));

  return {
    transportMs,
    processingMs,
    persistenceMs,
    otherMs,
    totalMs,
    dominant: totalMs <= 0 ? "unknown" : largest.key
  };
}
